import store from 'store'
import { setEnvParam, setTempEnvParam } from 'env'
import { sentrySetUser } from 'sentry'
import { store as reduxStore } from 'index'
import i18n from 'i18n'
import { confirmBase, errorBase, logout as logoutUI } from 'utils/ui'
import { SubscribePopup } from 'pages/auth/components/Login/loginEx'
import { notifyExtensionLogin } from 'utils/extensionBridge'

// notifyExtensionLogin 은 utils/extensionBridge 로 분리됨 (의존성 zero).
// 같은 함수를 여기서 재export — 기존 import 경로 유지.
export { notifyExtensionLogin }

/**
 * OAuth 인증 성공 시 공통 처리 로직
 * @param {Object} params - OAuth 응답 데이터
 * @param {string} params.username - 사용자 이름
 * @param {string} params.email - 사용자 이메일
 * @param {string} params.workspace_id - 워크스페이스 ID
 * @param {Object} params.officenext - OfficeNext 토큰 정보
 * @param {string} params.officenext.accessToken - OfficeNext 액세스 토큰
 * @param {Object} params.jwt - JWT 토큰 정보
 * @param {string} params.jwt.accessToken - JWT 액세스 토큰
 * @param {string} params.jwt.refreshToken - JWT 리프레시 토큰
 * @param {string} fallbackEmail - 폴백 이메일 (params에 없을 경우 사용)
 * @returns {Object} 처리된 인증 정보
 */
export function handleAuthSuccess(params, fallbackEmail = null) {
  const {
    username,
    email: responseEmail,
    workspace_id: workspaceId,
    officenext,
    jwt,
    user_uuid: userUuid,
  } = params

  // Provider Token 저장
  if (officenext?.accessToken) {
    store.set('app.user.providerToken', officenext.accessToken)
  }

  // 사용자 이메일 설정
  const userEmail = username || responseEmail || fallbackEmail
  if (userEmail) {
    setEnvParam('userEmail', userEmail)
    sentrySetUser(userEmail, responseEmail || fallbackEmail)
  }

  // Workspace ID 설정
  if (workspaceId) {
    setTempEnvParam('workspaceId', workspaceId)
    store.set('app.user.workspace_id', workspaceId)
  }

  // Phase 2.5: common-auth user_uuid 저장 → 이후 fingerprint = hashString(authUuid).
  // claim_token 콜백(routers/auth.py:638-661)이 params에 user_uuid를 포함해 전달한다.
  if (userUuid) {
    store.set('app.user.authUuid', userUuid)
  }

  // JWT 토큰 정보 반환
  return {
    jwt,
    userEmail,
    providerToken: officenext?.accessToken,
  }
}

/**
 * JWT 토큰 저장 및 Redux 로그인 액션 디스패치
 * @param {Object} jwt - JWT 토큰 정보
 * @param {string} jwt.accessToken - JWT 액세스 토큰
 * @param {string} jwt.refreshToken - JWT 리프레시 토큰
 * @param {string} providerToken - Provider 토큰
 */
export function saveTokenAndDispatchLogin(jwt, providerToken) {
  if (jwt?.accessToken) {
    store.set('app.user.token', jwt.accessToken)
    store.set('app.user.refreshToken', jwt.refreshToken)
    store.set('app.settings.isGrid', true)

    notifyExtensionLogin()

    reduxStore.dispatch({
      type: 'user/LOGIN_BY_TOKEN',
      payload: {
        tokenInfo: {
          provider_token: providerToken,
        },
      },
    })

    return true
  }
  return false
}

/**
 * OAuth 인증 에러 처리
 * @param {Object} params - 에러 응답 데이터
 * @param {number} params.code - 에러 코드
 * @param {string} params.message - 에러 메시지 (JSON 문자열)
 * @param {string} params.username - 사용자 이름
 * @param {string} params.provider_token - Provider 토큰
 * @param {string} params.role - 사용자 역할
 * @param {string} requestEmail - 요청한 이메일
 * @param {Function} notMatchingAccountHandler - 계정 불일치 처리 핸들러
 * @param {Function} [onContractExpiredClose] - 계약 만료(402) 안내 모달 확인/닫힘 시 콜백(officenext 로그아웃).
 *   순환참조(auth-callback ↔ services/landing) 회피 위해 호출처가 performSsoLogout 기반 콜백을 주입한다.
 * @returns {Object} 에러 정보 { shouldReject: boolean, error?: Error }
 */
export function handleAuthError(
  params,
  requestEmail,
  notMatchingAccountHandler,
  onContractExpiredClose,
) {
  const { code, message, username, provider_token: providerToken, role } = params

  let messageCode = code
  if (typeof message === 'object' && message?.code) {
    messageCode = message.code
  } else if (typeof message === 'string') {
    try {
      const parsed = JSON.parse(message)
      messageCode = parsed.code || code
    } catch {
      // JSON 파싱 실패 시 params.code 사용
    }
  }

  if (messageCode === 403) {
    // 구독 필요
    if (requestEmail && username !== requestEmail) {
      store.set('app.user.providerToken', providerToken)
      notMatchingAccountHandler(username)
    } else {
      SubscribePopup(i18n.t, role)
    }
    return { shouldReject: false }
  }

  if (messageCode === 409) {
    // 계정 불일치
    confirmBase({
      title: i18n.t('workspace.error.accountMismatch'),
      content: i18n.t('workspace.error.diffAccount'),
      onOk: logoutUI,
      okText: i18n.t('common.button.logout'),
    })
    return { shouldReject: false }
  }

  if (messageCode === 402) {
    // 계약(구독) 만료 — #4(사용중 890)와 동일한 안내 모달로 통일(구독 유도 팝업 폐기, OX-4282).
    // (기존: ExpiredSubscribePopup(i18n.t, role) — 이미지/구독·컨설팅 버튼)
    if (requestEmail && username !== requestEmail) {
      store.set('app.user.providerToken', providerToken)
      notMatchingAccountHandler(username)
    } else {
      errorBase({
        title: i18n.t('license.contractExpired'),
        onOk: onContractExpiredClose,
        onCancel: onContractExpiredClose,
      })
    }
    return { shouldReject: false }
  }

  // 인증 실패 (cancelled 등)
  errorBase({
    title: i18n.t('admin.activity.failedLogin'),
    content: i18n.t('login.error.ssoFailed'),
  })
  return { shouldReject: false }
}
